import {
  doc, getDoc, collection, query, where, orderBy, getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

// ── ADMIN STATS: THE READ SIDE ───────────────────────────────────────────────
// Every number on the dashboard is either one document or one small range
// query. Nothing here ever reads a collection to count it.
//
// THE COSTS, stated because they are the reason for the whole design:
//   lifetime totals   1 read           (stats/global)
//   a timeframe       ≤365 reads       (statsDaily, one small doc per day)
//   active users      1 read per 1000  (getCountFromServer over markers)
// The naive version — getDocs over `users` and over every generation — is one
// read per row, every time anyone opens the page, and grows forever.

// A FALLBACK ONLY. What a render costs depends on which model ran — the two
// Kontext tiers differ by about double — so the real rate is priced server-side
// per model (COST_USD_BY_MODEL in functions/statsAggregation.js) and stored as
// what was actually charged. The dashboard divides recorded spend by recorded
// generations to show the true rate, and reaches for this only when nothing has
// been generated yet and there is nothing to divide.
export const USD_PER_GENERATION = 0.04;

// ── WHAT EACH FLOW COSTS PER IMAGE ───────────────────────────────────────────
// The 3D-render path runs flux-kontext-pro, the staging path flux-kontext-max,
// and they are priced differently. Shown on the cards so a flow that has
// produced nothing yet still says what it WILL cost, instead of either lying
// about it or saying nothing.
//
// A MIRROR OF THE SERVER, AND KNOWINGLY SO. The prices live in
// functions/statsAggregation.js (COST_USD_BY_MODEL) and the flow→model mapping
// in functions/generateImageFlux.js; the browser cannot read either. Duplicated
// constants drift, so a test asserts these three files agree — change a price
// there and this fails until it is changed here too.
//
// Only used for the "before anything has been generated" case. Once a flow has
// spent something the card divides real spend by real count, which is the truth
// even if one of these numbers has gone stale.
export const FLOW_RATE_USD = {
  render: 0.04,
  staging: 0.08,
};

/** UTC day key, matching what the Cloud Functions write. */
export function dayKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

/** The first day of a timeframe, inclusive. */
export function startOfTimeframe(timeframe, now = new Date()) {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (timeframe === 'day') return d;
  if (timeframe === 'week') { d.setUTCDate(d.getUTCDate() - 6); return d; }
  if (timeframe === 'year') { d.setUTCDate(d.getUTCDate() - 364); return d; }
  d.setUTCDate(d.getUTCDate() - 29);              // month
  return d;
}

/** Every day key in a range, so days with no events still plot as zero. */
export function dayRange(fromKey, toKey) {
  const out = [];
  const d = new Date(`${fromKey}T00:00:00Z`);
  const end = new Date(`${toKey}T00:00:00Z`);
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** Lifetime totals. ONE document read, whatever the app's size. */
export async function fetchGlobalStats() {
  const snap = await getDoc(doc(db, 'stats', 'global'));
  const d = snap.exists() ? snap.data() : {};
  const customers = d.newUsers_customer || 0;
  const designers = d.newUsers_designer || 0;
  const shops = d.newUsers_shop || 0;
  return {
    totalUsers: d.newUsers || 0,
    // The split by account type, maintained by the same trigger that maintains
    // the total — so it costs nothing extra to read and is always consistent
    // with it. Written as `newUsers_<role>` by statsAggregation.js.
    customers,
    designers,
    shops,
    // Accounts created before the role split existed, or by a path that wrote
    // no userType. Surfaced rather than silently folded into "customers",
    // because a breakdown that does not add up to the total is a bug the
    // reader should be able to see rather than one they have to discover.
    unclassified: Math.max(0, (d.newUsers || 0) - customers - designers - shops),
    totalGenerations: d.generations || 0,
    totalCostUsd: d.costUsd || 0,
    // The same spend, split by which flow produced it — 3D renders and real-
    // room staging run different models at different prices, so one combined
    // figure cannot say whether a rising bill is more cheap renders or a few
    // more expensive stagings.
    renderGenerations: d.generations_render || 0,
    renderCostUsd: d.costUsd_render || 0,
    stagingGenerations: d.generations_staging || 0,
    stagingCostUsd: d.costUsd_staging || 0,
    // Lifetime, and never decremented when something is deleted — see
    // statsAggregation.js. "How many have ever been posted", the same way
    // totalUsers survives an account going away.
    totalPosts: d.posts || 0,
    // CURRENT counts, unlike the two above — a project or a listing can be
    // deleted, and these track what actually exists right now rather than
    // what has ever existed. See bumpGlobal in statsAggregation.js.
    projects: d.projects || 0,
    products: d.products || 0,
    updatedAt: d.updatedAt || null,
  };
}

/**
 * The per-day rows for a timeframe, zero-filled.
 *
 * A range query on document IDs, which needs no index: the ids ARE the dates,
 * lexicographically ordered because they are ISO. That is why the day key is
 * `YYYY-MM-DD` and not anything friendlier.
 */
export async function fetchDailyStats(timeframe, now = new Date()) {
  const from = dayKey(startOfTimeframe(timeframe, now));
  const to = dayKey(now);

  const snap = await getDocs(query(
    collection(db, 'statsDaily'),
    where('__name__', '>=', from),
    where('__name__', '<=', to),
    orderBy('__name__'),
  ));

  const byDay = new Map();
  snap.forEach((s) => byDay.set(s.id, s.data()));

  return dayRange(from, to).map((day) => {
    const d = byDay.get(day) || {};
    return {
      date: day,
      newUsers: d.newUsers || 0,
      newDesigners: d.newUsers_designer || 0,
      newShops: d.newUsers_shop || 0,
      newCustomers: d.newUsers_customer || 0,
      generations: d.generations || 0,
      // What was ACTUALLY charged, not a recalculation. If the per-image price
      // ever changes, history stays true rather than being rewritten by the
      // new constant.
      costUsd: d.costUsd || 0,
      // Per-flow, for the same reason as the lifetime totals above.
      renderGenerations: d.generations_render || 0,
      renderCostUsd: d.costUsd_render || 0,
      stagingGenerations: d.generations_staging || 0,
      stagingCostUsd: d.costUsd_staging || 0,
      activeUsers: d.activeUsers || 0,
      // How many gallery items were published that day — see onPostCreated.
      posts: d.posts || 0,
    };
  });
}

/**
 * Distinct users active TODAY, counted on the server.
 *
 * `getCountFromServer` bills one read per 1000 documents matched and never
 * transfers them, which is the difference between a number and a download.
 */
export async function fetchActiveToday(now = new Date()) {
  const snap = await getCountFromServer(
    collection(db, 'activity', dayKey(now), 'users'),
  );
  return snap.data().count;
}

/** GA4 traffic, via the admin-only callable. */
export async function fetchTraffic(timeframe) {
  const call = httpsCallable(functions, 'ga4Traffic');
  const res = await call({ timeframe });
  return res.data;
}

/** Promote or demote another account. Caller must already be an admin. */
export async function setAdminRole(uid, isAdmin = true) {
  const call = httpsCallable(functions, 'setAdminRole');
  const res = await call({ uid, admin: isAdmin });
  return res.data;
}
